import type { SearchSortBuf } from "~/classes/generated/search"
import type { SearchDefinition } from "~/models/polyschema/SearchDefinition"
import type { SearchFacet } from "~/models/polyschema/SearchFacet"
import type { SearchFacetType } from "~/models/polyschema/SearchFacetType"
import type { SearchSort } from "~/models/polyschema/SearchSort"

import { SearchBuf, SearchDefinitionBuf, SearchFacetBuf, SearchFacetTypeBuf, SearchPropertyBuf } from "~/classes/generated/search"

import { parseVector } from "./datagram"
import { addEntityPropertySelectionToBuf, addEntityPropertyTimestampToBuf, addEntityPropertyValueToBuf, asEntityPropertyStatusBuf, parseEntityPropertySchemaValue, parseEntityPropertySelection, parseEntityPropertyStatus, parseEntityPropertyTimestampBuf } from "./entity"
import { addFileIdToBuf, addFilePropertyIdToBuf, addSearchIdToBuf, addSearchPropertyIdToBuf, parseEntityPropertyId, parseFileId, parseFilePropertyId, parseSearchId } from "./id"

// NOTE: We don't need to parse or serialize the search query object,
// since we never use it on the client side and the server should be
// careful not to send it to the user (as the format is technically
// private to us)

export function parseSearchProperty(buf: SearchPropertyBuf) {
  // For some unknown reason (as of 11-7-2024), when using tauri channels, there is an
  // odd bug where the `valueArray` return null. While we figure that out, here is a kludge
  let value = buf.valueArray()
  if (!value?.length)
    value = new Uint8Array(parseVector(x => buf.value(x), buf.valueLength()))
  const searchProperty: SearchProperty = {
    searchId: parseSearchId(buf.searchId()!),
    propertyId: parseEntityPropertyId(buf.propertyId()!),
    version: buf.version(),
    schema: buf.schema(),
    value: parseEntityPropertySchemaValue(buf.schema(), value),
    timestamp: parseEntityPropertyTimestampBuf(buf.timestamp()!),
    deleted: buf.deleted(),
    status: parseEntityPropertyStatus(buf.status()),
    epochId: buf.epochId() ?? undefined,
  }
  return searchProperty
}

export function addSearchPropertyToBuf(
  fbb: DatagramBufferBuilder,
  property: SearchProperty,
) {
  const value = addEntityPropertyValueToBuf(fbb, property.value)
  const searchId = addSearchIdToBuf(fbb, property.searchId)
  const propertyId = addSearchPropertyIdToBuf(fbb, property.propertyId)
  const timestamp = addEntityPropertyTimestampToBuf(fbb, property.timestamp)

  SearchPropertyBuf.startSearchPropertyBuf(fbb)
  SearchPropertyBuf.addSearchId(fbb, searchId)
  SearchPropertyBuf.addPropertyId(fbb, propertyId)
  SearchPropertyBuf.addVersion(fbb, property.version)
  SearchPropertyBuf.addSchema(fbb, property.schema)
  SearchPropertyBuf.addValue(fbb, value)
  SearchPropertyBuf.addTimestamp(fbb, timestamp)
  if (property.deleted)
    SearchPropertyBuf.addDeleted(fbb, property.deleted)
  SearchPropertyBuf.addStatus(fbb, asEntityPropertyStatusBuf(property.status))
  if (property.epochId !== undefined)
    SearchPropertyBuf.addEpochId(fbb, property.epochId)
  return SearchPropertyBuf.endSearchPropertyBuf(fbb)
}

export function parseSearch(buf: SearchBuf): Search {
  const searchId = parseSearchId(buf.searchId()!)
  const selection = parseEntityPropertySelection(buf.selection())
  const properties: Record<string, SearchProperty> = {}
  for (let i = 0; i < buf.propertiesLength(); i++) {
    const property = parseSearchProperty(buf.properties(i)!)
    properties[property.propertyId] = property
  }
  return { searchId, selection, properties }
}

export function parseMaybeSearch(search: SearchBuf | null) {
  if (search)
    return parseSearch(search)
}

export function addSearchToBuf(fbb: DatagramBufferBuilder, search: Search) {
  const innerProperties = Object.values(search.properties).map(p => addSearchPropertyToBuf(fbb, p!))
  const properties = SearchBuf.createPropertiesVector(fbb, innerProperties)
  const searchId = addSearchIdToBuf(fbb, search.searchId)
  let selection
  if (search.selection)
    selection = addEntityPropertySelectionToBuf(fbb, search.selection)
  SearchBuf.startSearchBuf(fbb)
  SearchBuf.addSearchId(fbb, searchId)
  SearchBuf.addProperties(fbb, properties)
  if (selection)
    SearchBuf.addSelection(fbb, selection)
  return SearchBuf.endSearchBuf(fbb)
}

export function addSearchFacetTypeToBuf(fbb: DatagramBufferBuilder, facetType: SearchFacetType) {
  if ("exists" in facetType) {
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addExists(fbb, facetType.exists)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("equals" in facetType) {
    const equals = fbb.createString(facetType.equals)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addEquals(fbb, equals)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("notEquals" in facetType) {
    const notEquals = fbb.createString(facetType.notEquals)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addNotEquals(fbb, notEquals)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("startsWith" in facetType) {
    const startsWith = fbb.createString(facetType.startsWith)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addStartsWith(fbb, startsWith)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("contains" in facetType) {
    const contains = fbb.createString(facetType.contains)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addContains(fbb, contains)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("fileIn" in facetType) {
    const fileIn = addFileIdToBuf(fbb, facetType.fileIn)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addFileIn(fbb, fileIn)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("fileOnlyIn" in facetType) {
    const fileOnlyIn = addFileIdToBuf(fbb, facetType.fileOnlyIn)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addFileOnlyIn(fbb, fileOnlyIn)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("greaterThan" in facetType) {
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addGreaterThan(fbb, facetType.greaterThan)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("greaterThanEqual" in facetType) {
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addGreaterThanEqual(fbb, facetType.greaterThanEqual)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("lessThan" in facetType) {
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addLessThan(fbb, facetType.lessThan)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("lessThanEqual" in facetType) {
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addLessThanEqual(fbb, facetType.lessThanEqual)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("afterDate" in facetType) {
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addAfterDate(fbb, BigInt(facetType.afterDate))
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("onOrAfterDate" in facetType) {
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addOnOrAfterDate(fbb, BigInt(facetType.onOrAfterDate))
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("beforeDate" in facetType) {
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addBeforeDate(fbb, BigInt(facetType.beforeDate))
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("onOrBeforeDate" in facetType) {
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addOnOrBeforeDate(fbb, BigInt(facetType.onOrBeforeDate))
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("nearFile" in facetType) {
    const nearFile = addFileIdToBuf(fbb, facetType.nearFile)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addNearFile(fbb, nearFile)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("nearImageText" in facetType) {
    const nearImageText = fbb.createString(facetType.nearImageText)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addNearImageText(fbb, nearImageText)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("nearAudioText" in facetType) {
    const nearAudioText = fbb.createString(facetType.nearAudioText)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addNearAudioText(fbb, nearAudioText)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("nearDocLikeText" in facetType) {
    const nearDocLikeText = fbb.createString(facetType.nearDocLikeText)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addNearDocLikeText(fbb, nearDocLikeText)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("nearVideoText" in facetType) {
    const nearVideoText = fbb.createString(facetType.nearVideoText)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addNearVideoText(fbb, nearVideoText)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("nearColor" in facetType) {
    const nearColor = fbb.createString(facetType.nearColor)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addNearColor(fbb, nearColor)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else if ("hasTag" in facetType) {
    const hasTag = fbb.createString(facetType.hasTag)
    SearchFacetTypeBuf.startSearchFacetTypeBuf(fbb)
    SearchFacetTypeBuf.addHasTag(fbb, hasTag)
    return SearchFacetTypeBuf.endSearchFacetTypeBuf(fbb)
  }
  else {
    console.error("Unknown facet type", facetType)
    throw new Error("Unknown facet type")
  }
}
export function addSearchFacetToBuf(fbb: DatagramBufferBuilder, facet: SearchFacet) {
  const propertyId = addFilePropertyIdToBuf(fbb, facet.propertyId)
  const facetTypeBuffer = addSearchFacetTypeToBuf(fbb, facet.facetType)
  SearchFacetBuf.startSearchFacetBuf(fbb)
  SearchFacetBuf.addPropertyId(fbb, propertyId)
  SearchFacetBuf.addFacetType(fbb, facetTypeBuffer)
  return SearchFacetBuf.endSearchFacetBuf(fbb)
}
export function addSearchDefinitionToBuf(fbb: DatagramBufferBuilder, definition: SearchDefinition) {
  const facetBuffers = definition.facets.map(facet => addSearchFacetToBuf(fbb, facet))
  const facets = SearchDefinitionBuf.createFacetsVector(fbb, facetBuffers)
  SearchDefinitionBuf.startSearchDefinitionBuf(fbb)
  SearchDefinitionBuf.addFacets(fbb, facets)
  return SearchDefinitionBuf.endSearchDefinitionBuf(fbb)
}

export function parseSearchFacetType(buf: SearchFacetTypeBuf): SearchFacetType {
  if (buf.exists())
    return { exists: buf.exists()! }
  else if (isDefined(buf.equals()))
    return { equals: buf.equals()! }
  else if (isDefined(buf.notEquals()))
    return { notEquals: buf.notEquals()! }
  else if (isDefined(buf.startsWith()))
    return { startsWith: buf.startsWith()! }
  else if (isDefined(buf.contains()))
    return { contains: buf.contains()! }
  else if (isDefined(buf.fileIn()))
    return { fileIn: parseFileId(buf.fileIn()!) }
  else if (isDefined(buf.fileOnlyIn()))
    return { fileOnlyIn: parseFileId(buf.fileOnlyIn()!) }
  else if (isDefined(buf.greaterThan()))
    return { greaterThan: Number(buf.greaterThan()) }
  else if (isDefined(buf.greaterThanEqual()))
    return { greaterThanEqual: Number(buf.greaterThanEqual()) }
  else if (isDefined(buf.lessThan()))
    return { lessThan: Number(buf.lessThan()) }
  else if (isDefined(buf.lessThanEqual()))
    return { lessThanEqual: Number(buf.lessThanEqual()) }
  else if (isDefined(buf.afterDate()))
    return { afterDate: Number(buf.afterDate()!) }
  else if (isDefined(buf.onOrAfterDate()))
    return { onOrAfterDate: Number(buf.onOrAfterDate()!) }
  else if (isDefined(buf.beforeDate()))
    return { beforeDate: Number(buf.beforeDate()!) }
  else if (isDefined(buf.onOrBeforeDate()))
    return { onOrBeforeDate: Number(buf.onOrBeforeDate()!) }
  else if (isDefined(buf.nearFile()))
    return { nearFile: parseFileId(buf.nearFile()!) }
  else if (isDefined(buf.nearImageText()))
    return { nearImageText: buf.nearImageText()! }
  else if (isDefined(buf.nearAudioText()))
    return { nearAudioText: buf.nearAudioText()! }
  else if (isDefined(buf.nearDocLikeText()))
    return { nearDocLikeText: buf.nearDocLikeText()! }
  else if (isDefined(buf.nearVideoText()))
    return { nearVideoText: buf.nearVideoText()! }
  else if (isDefined(buf.nearColor()))
    return { nearColor: buf.nearColor()! }
  else if (isDefined(buf.hasTag()))
    return { hasTag: buf.hasTag()! }

  console.error("unknown facet type", buf)
  throw new Error("Unknown facet type")
}

export function parseSearchFacet(buf: SearchFacetBuf): SearchFacet {
  const propertyId = parseFilePropertyId(buf.propertyId()!)
  const facetType = parseSearchFacetType(buf.facetType()!)
  return { propertyId, facetType }
}

export function parseSearchSort(buf: SearchSortBuf): SearchSort {
  const propertyId = parseFilePropertyId(buf.propertyId()!)
  const descending = buf.descending()
  return { propertyId, descending }
}

export function parseSearchDefinition(buf: SearchDefinitionBuf): SearchDefinition {
  const facets = parseVector(i => parseSearchFacet(buf.facets(i)!), buf.facetsLength())
  const sorts = parseVector(i => parseSearchSort(buf.sorts(i)!), buf.sortsLength())
  return { facets, sorts }
}
